/**
 * ServicePageData for the "Customer Acquisition" page (route: /customer-acquisition/).
 *
 * Content is sourced from 01-new-customer-acquisition-engagement.md (the primary
 * content source — every paragraph/heading/list item below is that file's wording,
 * unchanged in meaning; the only trim is dropping the redundant "RESIDENT - " prefix
 * from the database section's heading, since that section now shows a "RESIDENT"
 * badge graphic right next to it — see the note on that section below).
 *
 * 01-new-customer-acquisition.html (and the matching design image) was used for visual/
 * layout guidance only: the hero's left-aligned layout with inline highlight stats, the
 * dark "RESIDENT database" callout card, the icon-topped metrics grid, the two-column
 * compact info cards, the industry tags, and the bordered business-case checklist. None
 * of its markup, fonts, or navy/teal colours were copied — colours use the site's
 * existing pink (#db0058) / charcoal (#232528) palette, and the icon shapes (postal/
 * landline/email/mobile) reuse the exact glyph paths from that file, recoloured to match.
 *
 * @type {import('../../components/service/ServicePageLayout').ServicePageData}
 */
const customerAcquisitionPageData = {
  slug: 'customer-acquisition',
  title: 'New Customer Acquisition & Engagement',
  introduction:
    'Highly targeted acquisition strategies with deeper & meaningful customer engagement with industry-leading data and predictive modelling.',
  hero: {
    eyebrow: 'Customer Acquisition',
    align: 'left',
    // The MD states both figures in prose (see the "resident-database" and
    // "targeted-marketing-data" sections below) — surfaced here as a compact
    // highlight row, matching the reference design, not new information.
    highlights: [
      { value: '50M+', label: 'Records in RESIDENT' },
      { value: '500+', label: 'Targeting variables' },
      { value: 'Multi-channel', label: 'Online & offline reach' },
    ],
    // Links to the enquiry form at the bottom of this same page — see enquiryForm.anchorId below.
    cta: { label: 'Enquire now', href: '#enquiry' },
  },

  sections: [
    {
      id: 'resident-database',
      variant: 'feature',
      badge: { text: 'RESIDENT', subtext: 'Database' },
      // Original MD heading is "RESIDENT - The UK's Most Comprehensive Prospect Database";
      // the "RESIDENT - " prefix is dropped here only because the badge beside it already
      // says "RESIDENT" — no other wording changed.
      title: "The UK's Most Comprehensive Prospect Database",
      content: [
        'RESIDENT is the most comprehensive, accurate, compliant and responsive consumer prospect data available in the UK, providing businesses with the right data to acquire, engage and retain new customers. We hold over 50 million records across multiple channels, sourced from a wide range of sources including lifestyle, digital and transactional data, as well as public domain information available at the postcode level.',
        'Our RESIDENT database can be tailored to a truly granular level, giving you the confidence to accurately communicate with relevant audience segments in the right way, build higher quality customer relationships, reduce waste and increase marketing ROI.',
      ],
    },
    // Sourced from the MD's "Database Coverage" list under the RESIDENT section — the
    // only numeric figures given in the source content. Placed here (rather than via the
    // top-level `stats` field) so it renders between the RESIDENT card and the two info
    // cards below, matching the reference layout.
    {
      type: 'stats',
      variant: 'cards',
      items: [
        { value: '40M+', label: 'Postal addresses', icon: 'home' },
        { value: '8M+', label: 'Landline telephone numbers', icon: 'phone' },
        { value: '7M+', label: 'Email addresses', icon: 'mail' },
        { value: '14M+', label: 'Mobile numbers', icon: 'mobile' },
      ],
    },
    {
      id: 'overview',
      group: 'approach',
      title: 'Overview',
      content: [
        'Through understanding your prospect, acquisition and engagement needs, we use intelligent planning, profiling and modelling to run multi-channel campaign activity across online and offline channels to identify and acquire new customers.',
        'We will also help you enhance engagement with existing customers through data validation to enrich and increase your customer interaction experience and eliminate wasteful contact methods.',
      ],
    },
    {
      id: 'targeted-marketing-data',
      group: 'approach',
      title: 'Targeted Marketing Data',
      content: [
        'With over 500 targeting variables across 50 million prospect records, our RESIDENT database can be tailored to target key individuals and customer demographics most relevant to your outreach campaign. Our marketing data helps you to quickly find the most relevant prospects, those people who are highly likely to lead to a conversion. This approach enables you to save on your marketing spend.',
        'All the data we hold in our RESIDENT database is collected specifically for marketing purposes and is fully GDPR compliant. You can build your marketing data requirements based on people who look like your most valuable existing customers, maximising the potential of your marketing campaign to increase your sales whilst cutting out unnecessary waste.',
      ],
    },
    {
      id: 'industry-verticals',
      title: 'Target by Industry Vertical and Campaign',
      content: [
        'Our RESIDENT database can be used to create customised prospect marketing lists for specific industries, including:',
      ],
      list: ['Insurance', 'Energy', 'Telecoms', 'Charity', 'Financial Services', 'Home Improvement', 'Travel & Entertainment'],
      listStyle: 'tags',
      afterContent: [
        "Campaign relevance can be supported with the right data that is targeted to best suit the needs of consumers. For example, reaching people for a pet-related service, we'll help you reach people who actually have pets in their household. This example approach that we apply to many sectors and verticals helps your campaign performance and reduces the likelihood of wasted campaign effort and cost.",
      ],
    },
  ],

  // Derived from the MD's "The Business Case for Using RESIDENT" bullet list. Each bullet's
  // wording is preserved verbatim; where a bullet has a natural clause break (via "through",
  // "enabling", "such as") it's split into title + description at that exact point, and the
  // checklist variant re-joins them onto one line (matching the reference's single-line
  // bullets). Bullets with no natural break have no description rather than an invented one.
  benefits: {
    heading: 'The Business Case for Using RESIDENT',
    variant: 'checklist',
    items: [
      {
        title: 'Save business time and effort',
        description: 'through pre-verified and up-to-date prospect information.',
      },
      {
        title: 'Access to a mass comprehensive database of potential customers,',
        description: 'enabling highly personalised and cost-effective marketing campaigns.',
      },
      {
        title: 'Increased conversion and campaign performance potential.',
      },
      {
        title: 'Fixes campaign issues',
        description: 'such as low response rates, ineffective lead generation, and sourcing accurate prospect information.',
      },
      {
        title: 'Meets and exceeds the regulatory requirements of business.',
      },
    ],
  },

  pricing: null,

  enquiryForm: {
    enabled: true,
    serviceName: 'Customer Acquisition',
    anchorId: 'enquiry',
    heading: 'Enquire now',
  },

  seo: {
    title: 'New Customer Acquisition & Engagement - Lloyd James Data Consultancy',
    description:
      'Acquire and engage new customers with RESIDENT — a GDPR-compliant UK prospect database of 50M+ records and 500+ targeting variables for multi-channel campaigns.',
    keywords:
      'customer acquisition, customer engagement, RESIDENT database, prospect data, targeted marketing data, GDPR compliant marketing data, Lloyd James',
    canonical: '/customer-acquisition/',
  },
};

export default customerAcquisitionPageData;
